/**
 * Replica API Instance
 * 
 * Used for replica (public) pages with referral tracking
 * Phase 2-B: Session Cookie Authentication (Hardened)
 * 
 * NOTE: Replica pages are mostly public. We do NOT force logout on 401.
 * We just reject the error and let the component handle it.
 */

import axios from "axios";
import { BASE_URL, DEFAULT_KEY } from "../config/config";

// ============================================================================
// 401 HARDENING HELPERS (shared logic with api.js)
// ============================================================================

/**
 * Extract legacy error code from various response shapes
 * @param {Object} error - Axios error object
 * @returns {{ numericCode: number|null, stringCode: string|null }}
 */
const extractLegacyCode = (error) => {
  const response = error?.response;
  
  const rawCode = 
    response?.data?.data?.data?.code ||
    response?.data?.data?.code ||
    response?.data?.code ||
    null;
  
  let numericCode = null;
  if (rawCode !== null) {
    const parsed = typeof rawCode === 'number' ? rawCode : parseInt(rawCode, 10);
    if (!isNaN(parsed)) {
      numericCode = parsed;
    }
  }
  
  return { numericCode };
};

// ============================================================================
// AXIOS INSTANCE
// ============================================================================

const instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Required for session cookies
});

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

instance.interceptors.response.use(
  (response) => {
    if (response.status === 200) {
      return response;
    }
  },
  (error) => {
    const status = error?.response?.status;
    
    if (status === 401) {
      const { numericCode } = extractLegacyCode(error);
      
      // Special case: error code 1042 - preserve EXACT existing behavior
      if (numericCode === 1042) {
        return error.response?.data?.data;
      }
      
      // For replica (public) pages: do NOT force logout on 401
      // Just reject and let the component handle it
      return Promise.reject(error);
      
    } else {
      // Other errors - return data if available
      return error.response?.data;
    }
  }
);

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

instance.interceptors.request.use((config) => {
  config.headers["Accept"] = "application/json";
  
  // Don't set Content-Type for FormData - let the browser set it with boundary
  // This is critical for file uploads (e.g., bank receipts)
  if (!(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  }
  
  // Keep api-key header
  config.headers["api-key"] = DEFAULT_KEY;

  // Add referral tracking params for replica pages
  if (config.url.includes('replica-home') || config.url.includes('replica-register-get')) {
    config.params = {
      ...config.params,
      referralId: localStorage.getItem('referralId'),
      hash: localStorage.getItem('hashKey')
    };
  }
  
  return config;
});

const replicaAPI = instance;

export default replicaAPI;
